import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../services/api';
import OperatorForm from './OperatorForm';
import OperatorImportModal from './OperatorImportModal';
import './SharedMailboxesTab.css';

function SharedMailboxesTab({ users, signatures, onRefresh }) {
    const [sharedMailboxes, setSharedMailboxes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedMailbox, setExpandedMailbox] = useState(null);
    const [operators, setOperators] = useState({});
    const [loadingOperators, setLoadingOperators] = useState({});

    // Modals
    const [showAddOperatorModal, setShowAddOperatorModal] = useState(false);
    const [showEditOperatorModal, setShowEditOperatorModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showMarkAsSharedModal, setShowMarkAsSharedModal] = useState(false);
    const [selectedMailbox, setSelectedMailbox] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);

    // Assign signature modal
    const [showAssignSignatureModal, setShowAssignSignatureModal] = useState(false);
    const [selectedSignatureId, setSelectedSignatureId] = useState('');

    const loadSharedMailboxes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.getSharedMailboxes();
            if (response && response.data) {
                setSharedMailboxes(response.data.mailboxes || []);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des boîtes partagées:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSharedMailboxes();
    }, [loadSharedMailboxes]);

    const loadOperators = async (mailboxId) => {
        if (operators[mailboxId]) return; // Déjà chargé

        try {
            setLoadingOperators(prev => ({ ...prev, [mailboxId]: true }));
            const response = await API.getMailboxOperators(mailboxId);
            if (response && response.data) {
                setOperators(prev => ({
                    ...prev,
                    [mailboxId]: response.data.operators || []
                }));
            }
        } catch (error) {
            console.error('Erreur lors du chargement des opérateurs:', error);
        } finally {
            setLoadingOperators(prev => ({ ...prev, [mailboxId]: false }));
        }
    };

    const toggleMailbox = (mailboxId) => {
        if (expandedMailbox === mailboxId) {
            setExpandedMailbox(null);
        } else {
            setExpandedMailbox(mailboxId);
            loadOperators(mailboxId);
        }
    };

    const handleMarkAsShared = async (userId) => {
        try {
            await API.toggleSharedMailbox(userId, true);
            await loadSharedMailboxes();
            if (onRefresh) onRefresh();
            setShowMarkAsSharedModal(false);
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleUnmarkAsShared = async (mailbox) => {
        if (!window.confirm(`Retirer "${mailbox.email}" des boîtes partagées ? Les opérateurs associés seront supprimés.`)) {
            return;
        }

        try {
            await API.toggleSharedMailbox(mailbox.id, false);
            await loadSharedMailboxes();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleCreateOperator = async (operatorData) => {
        try {
            await API.createOperator(selectedMailbox.id, operatorData);
            // Recharger les opérateurs
            setOperators(prev => ({ ...prev, [selectedMailbox.id]: null }));
            await loadOperators(selectedMailbox.id);
            await loadSharedMailboxes();
            setShowAddOperatorModal(false);
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleUpdateOperator = async (operatorData) => {
        try {
            await API.updateOperator(selectedOperator.id, operatorData);
            // Recharger les opérateurs
            setOperators(prev => ({ ...prev, [selectedMailbox.id]: null }));
            await loadOperators(selectedMailbox.id);
            setShowEditOperatorModal(false);
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleDeleteOperator = async (operator) => {
        if (!window.confirm(`Supprimer l'opérateur "${operator.first_name} ${operator.last_name}" ?`)) {
            return;
        }

        try {
            await API.deleteOperator(operator.id);
            // Recharger les opérateurs
            setOperators(prev => ({ ...prev, [expandedMailbox]: null }));
            await loadOperators(expandedMailbox);
            await loadSharedMailboxes();
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleImportComplete = async () => {
        // Recharger les opérateurs
        setOperators(prev => ({ ...prev, [selectedMailbox.id]: null }));
        await loadOperators(selectedMailbox.id);
        await loadSharedMailboxes();
        setShowImportModal(false);
    };

    const handleAssignSignature = async () => {
        if (!selectedSignatureId) return;

        try {
            await API.assignSignatureToUser(selectedMailbox.id, parseInt(selectedSignatureId));
            await loadSharedMailboxes();
            setShowAssignSignatureModal(false);
            setSelectedSignatureId('');
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    const handleRemoveSignature = async (mailbox, signatureId) => {
        try {
            await API.removeSignatureFromUser(mailbox.id, signatureId);
            await loadSharedMailboxes();
        } catch (error) {
            console.error('Erreur:', error);
            alert(error.message);
        }
    };

    // Filtrer les utilisateurs qui ne sont pas déjà des boîtes partagées
    const nonSharedUsers = users.filter(u => !u.is_shared_mailbox);

    if (loading) {
        return (
            <div className="shared-mailboxes-loading">
                <div className="spinner"></div>
                <span>Chargement des boîtes partagées...</span>
            </div>
        );
    }

    return (
        <div className="shared-mailboxes-tab">
            <div className="shared-mailboxes-header">
                <div className="header-info">
                    <h3>Boîtes mail partagées</h3>
                    <p>
                        Gérez les adresses mail partagées par plusieurs opérateurs (ex: support@, contact@).
                        Chaque opérateur dispose de son propre matricule pour personnaliser sa signature.
                    </p>
                </div>
                <button
                    className="btn-primary"
                    onClick={() => setShowMarkAsSharedModal(true)}
                    disabled={nonSharedUsers.length === 0}
                >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Ajouter une boîte partagée
                </button>
            </div>

            {sharedMailboxes.length === 0 ? (
                <div className="shared-mailboxes-empty">
                    <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h4>Aucune boîte partagée</h4>
                    <p>
                        Marquez une adresse mail de l'annuaire comme partagée pour gérer ses opérateurs.
                    </p>
                </div>
            ) : (
                <div className="shared-mailboxes-list">
                    {sharedMailboxes.map(mailbox => (
                        <div key={mailbox.id} className="shared-mailbox-card">
                            <div
                                className="mailbox-header"
                                onClick={() => toggleMailbox(mailbox.id)}
                            >
                                <div className="mailbox-info">
                                    <div className="mailbox-icon">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="mailbox-details">
                                        <span className="mailbox-email">{mailbox.email}</span>
                                        <span className="mailbox-name">
                                            {mailbox.first_name} {mailbox.last_name}
                                        </span>
                                    </div>
                                </div>
                                <div className="mailbox-stats">
                                    <span className="stat-badge operators">
                                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        {mailbox.operator_count || 0} opérateur(s)
                                    </span>
                                    {mailbox.assigned_signatures?.length > 0 && (
                                        <span className="stat-badge signatures">
                                            {mailbox.assigned_signatures.length} signature(s)
                                        </span>
                                    )}
                                    <svg
                                        className={`expand-icon ${expandedMailbox === mailbox.id ? 'expanded' : ''}`}
                                        width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            {expandedMailbox === mailbox.id && (
                                <div className="mailbox-content">
                                    {/* Signatures assignées */}
                                    <div className="mailbox-signatures-section">
                                        <div className="section-header">
                                            <h4>Signatures assignées</h4>
                                            <button
                                                className="btn-small"
                                                onClick={() => {
                                                    setSelectedMailbox(mailbox);
                                                    setShowAssignSignatureModal(true);
                                                }}
                                            >
                                                + Assigner
                                            </button>
                                        </div>
                                        {mailbox.assigned_signatures?.length > 0 ? (
                                            <div className="assigned-signatures-list">
                                                {mailbox.assigned_signatures.map(sig => (
                                                    <span key={sig.id} className="signature-tag">
                                                        {sig.name}
                                                        <button
                                                            className="remove-btn"
                                                            onClick={() => handleRemoveSignature(mailbox, sig.id)}
                                                        >
                                                            &times;
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="no-signatures">Aucune signature assignée</p>
                                        )}
                                    </div>

                                    {/* Opérateurs */}
                                    <div className="mailbox-operators-section">
                                        <div className="section-header">
                                            <h4>Opérateurs ({operators[mailbox.id]?.length || 0})</h4>
                                            <div className="section-actions">
                                                <button
                                                    className="btn-small btn-secondary"
                                                    onClick={() => {
                                                        setSelectedMailbox(mailbox);
                                                        setShowImportModal(true);
                                                    }}
                                                >
                                                    Importer CSV
                                                </button>
                                                <button
                                                    className="btn-small"
                                                    onClick={() => {
                                                        setSelectedMailbox(mailbox);
                                                        setShowAddOperatorModal(true);
                                                    }}
                                                >
                                                    + Ajouter
                                                </button>
                                            </div>
                                        </div>

                                        {loadingOperators[mailbox.id] ? (
                                            <div className="loading-operators">
                                                <div className="spinner-small"></div>
                                                <span>Chargement...</span>
                                            </div>
                                        ) : operators[mailbox.id]?.length > 0 ? (
                                            <table className="operators-table">
                                                <thead>
                                                    <tr>
                                                        <th>Matricule</th>
                                                        <th>Nom</th>
                                                        <th>Fonction</th>
                                                        <th>Téléphone</th>
                                                        <th>Statut</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {operators[mailbox.id].map(op => (
                                                        <tr key={op.id} className={!op.is_active ? 'inactive' : ''}>
                                                            <td className="matricule">{op.matricule}</td>
                                                            <td>{op.first_name} {op.last_name}</td>
                                                            <td>{op.title || '-'}</td>
                                                            <td>{op.phone || op.mobile || '-'}</td>
                                                            <td>
                                                                <span className={`status-badge ${op.is_active ? 'active' : 'inactive'}`}>
                                                                    {op.is_active ? 'Actif' : 'Inactif'}
                                                                </span>
                                                            </td>
                                                            <td className="actions">
                                                                <button
                                                                    className="btn-icon"
                                                                    title="Modifier"
                                                                    onClick={() => {
                                                                        setSelectedMailbox(mailbox);
                                                                        setSelectedOperator(op);
                                                                        setShowEditOperatorModal(true);
                                                                    }}
                                                                >
                                                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    className="btn-icon danger"
                                                                    title="Supprimer"
                                                                    onClick={() => handleDeleteOperator(op)}
                                                                >
                                                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <p className="no-operators">
                                                Aucun opérateur. Ajoutez des opérateurs manuellement ou importez-les depuis un fichier CSV.
                                            </p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="mailbox-actions">
                                        <button
                                            className="btn-danger-outline"
                                            onClick={() => handleUnmarkAsShared(mailbox)}
                                        >
                                            Retirer des boîtes partagées
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal: Marquer comme partagée */}
            {showMarkAsSharedModal && (
                <div className="modal-overlay" onClick={() => setShowMarkAsSharedModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Ajouter une boîte partagée</h3>
                            <button className="modal-close" onClick={() => setShowMarkAsSharedModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>Sélectionnez une adresse mail de l'annuaire à marquer comme partagée :</p>
                            <div className="users-list-modal">
                                {nonSharedUsers.map(user => (
                                    <div
                                        key={user.id}
                                        className="user-item"
                                        onClick={() => handleMarkAsShared(user.id)}
                                    >
                                        <span className="user-email">{user.email}</span>
                                        <span className="user-name">{user.first_name} {user.last_name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Ajouter opérateur */}
            {showAddOperatorModal && selectedMailbox && (
                <OperatorForm
                    mailbox={selectedMailbox}
                    onSubmit={handleCreateOperator}
                    onClose={() => setShowAddOperatorModal(false)}
                />
            )}

            {/* Modal: Modifier opérateur */}
            {showEditOperatorModal && selectedMailbox && selectedOperator && (
                <OperatorForm
                    mailbox={selectedMailbox}
                    operator={selectedOperator}
                    onSubmit={handleUpdateOperator}
                    onClose={() => {
                        setShowEditOperatorModal(false);
                        setSelectedOperator(null);
                    }}
                />
            )}

            {/* Modal: Import CSV */}
            {showImportModal && selectedMailbox && (
                <OperatorImportModal
                    mailbox={selectedMailbox}
                    onImportComplete={handleImportComplete}
                    onClose={() => setShowImportModal(false)}
                />
            )}

            {/* Modal: Assigner signature */}
            {showAssignSignatureModal && selectedMailbox && (
                <div className="modal-overlay" onClick={() => setShowAssignSignatureModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Assigner une signature</h3>
                            <button className="modal-close" onClick={() => setShowAssignSignatureModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>Assigner une signature à <strong>{selectedMailbox.email}</strong></p>
                            <select
                                value={selectedSignatureId}
                                onChange={e => setSelectedSignatureId(e.target.value)}
                                className="signature-select"
                            >
                                <option value="">-- Sélectionner --</option>
                                {signatures
                                    .filter(s => !selectedMailbox.assigned_signatures?.some(as => as.id === s.id))
                                    .map(sig => (
                                        <option key={sig.id} value={sig.id}>{sig.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowAssignSignatureModal(false)}>
                                Annuler
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleAssignSignature}
                                disabled={!selectedSignatureId}
                            >
                                Assigner
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SharedMailboxesTab;
